import type { discountPrice, tCrossSellSection, tProduct, tProductPreview } from 'src/shared/frontendTypes/product'
import type { tItem, tItems } from 'src/shared/frontendTypes/warenkorb'
import { isDiscountDateActive } from 'src/shared/discountShop'
import { getCartPriceAmount, isCartPriceLockValid } from 'src/functions/cartPriceLock'
import getCombination from 'src/pages/Product/functions/getCombination'
import getImages from 'src/pages/Product/functions/getImages'
import { buildCurrentTechnicalMeta, normalizeText } from 'src/pages/Product/functions/crossSellVisibility'
import type { CommerceRecommendationContext } from 'src/shared/commerceAnalytics'

export type CartRecommendation = { product: tProductPreview; reason: string; recommendation?: CommerceRecommendationContext }
export type CartRecommendationSection = tCrossSellSection & { recommendation?: CommerceRecommendationContext }

/** Towel parts are frequently sizes of one product, rather than separate
 * catalog records. Keep every non-size selection (especially colour) fixed. */
export const sameProductComplements = (product: tProduct, sourceKey: string): CartRecommendation[] => {
  const source = product.combinations.find(combination => combination.key === sourceKey)
  if (!source) return []
  const towelPart = (labels: string[]) => labels.join(' ').toLowerCase()
    .match(/waschhandschuh|waschlappen|seifenlappen|gästetuch|gaestetuch|duschtuch|duschhandtuch|badetuch|saunatuch|handtuch/)?.[0]
  const sourcePart = towelPart(source.attributes)
  if (!sourcePart) return []
  const attributes = product.attributes || []
  const varyingAttributes = attributes.filter(attribute => /größe|groesse|size|format|ausführung|ausfuehrung|typ|art/i.test(attribute.name))
  if (!varyingAttributes.length) return []
  const fixedOptions = attributes.filter(attribute => !varyingAttributes.includes(attribute))
    .flatMap(attribute => attribute.options.filter(option => source.options.includes(option.id)).map(option => option.id))
  const currentMeta = buildCurrentTechnicalMeta(product, product.crossSellTechnicalTags, {}, source)
  return product.combinations
    .filter(candidate => candidate.key !== source.key && fixedOptions.every(option => candidate.options.includes(option)))
    .filter(candidate => {
      const part = towelPart(candidate.attributes)
      if (!part || part === sourcePart) return false
      const meta = buildCurrentTechnicalMeta(product, product.crossSellTechnicalTags, {}, candidate)
      return (!currentMeta.farbe || !meta.farbe || normalizeText(currentMeta.farbe) === normalizeText(meta.farbe))
        && (!currentMeta.serie || !meta.serie || normalizeText(currentMeta.serie) === normalizeText(meta.serie))
    })
    .filter(candidate => buildRecommendationCartItem(product, candidate.key, {}) !== null)
    .sort((a, b) => Number((b.stock || 0) > 0) - Number((a.stock || 0) > 0))
    .map(candidate => ({
      reason: 'Gleiche Serie · gleiche Farbe',
      recommendation: { surface: 'cart', sourceProductId: product.id, sourceVariantKey: sourceKey, section: 'serie' },
      product: {
        id: product.id, key: candidate.key, name: product.name, path: product.path, metaTitle: product.metaTitle,
        brand: product.brand, brandTitel: product.brandTitel, brandMetaTitel: product.brandMetaTitel,
        categorie: product.categorie, price: candidate.price, discount: candidate.discount, priceLock: candidate.priceLock,
        attributes: candidate.attributes, options: candidate.options,
        images: getImages(product, candidate, candidate.options),
        deliverytime: candidate.deliverytime, stockDeliverytime: candidate.stockDeliverytime, stock: candidate.stock,
        shippingType: product.shippingType, onlyStock: product.onlyStock, purchaseMode: product.purchaseMode,
        inquireOnly: product.inquireOnly, hash: candidate.hash,
      },
    }))
}

export const recommendationPrice = (preview: tProductPreview): number | discountPrice => {
  const discount = preview.discount
  return discount && isDiscountDateActive({ dateEnd: discount.dateEnd })
    ? {
      regularPrice: preview.price,
      discountPrice: discount.price,
      discountPriceAfterStock: discount.priceAfterStock,
      stockDiscountLimit: discount.stockDiscountLimit,
      limitStockDiscountToStock: discount.limitStockDiscountToStock,
      quantityDiscount: discount.quantityDiscount,
      title: discount.metaTitle,
      type: discount.type,
      dateEnd: discount.dateEnd,
      showPercent: discount.showPercent,
    }
    : preview.price
}

/** The API resolves the selected colour for series; accessories and additions
 * are explicitly maintained catalog relationships. Never use alternatives. */
export const selectCartRecommendations = (
  sections: CartRecommendationSection[],
  items: tItems,
  limit = 4,
  preferred: CartRecommendation[] = [],
): CartRecommendation[] => {
  const existing = new Set(Object.values(items).map(item => `${item.id}:${item.key}`))
  const seen = new Set<string>()
  const result: CartRecommendation[] = []
  const ordered = [...sections].sort((a, b) => Number(b.art === 'serie') - Number(a.art === 'serie'))
  const candidates = [...preferred, ...ordered
    .filter(section => ['serie', 'zubehoer', 'ergaenzung'].includes(section.art))
    .flatMap(section => section.products.map(product => ({ product,
      recommendation: section.recommendation,
      reason: section.art === 'serie' ? 'Passend kombiniert'
        : section.art === 'zubehoer' ? 'Passendes Zubehör' : 'Passende Ergänzung',
    }))) ]
    for (const candidate of candidates) {
      const { product } = candidate
      const key = `${product.id}:${product.key}`
      if (existing.has(key) || seen.has(key) || !product.key || !(product.images[0]?.sm || product.images[0]?.xs)) continue
      if (product.inquireOnly || product.purchaseMode === 'inquiry') continue
      if (product.onlyStock && (product.stock || 0) < 1) continue
      if ((product.stock || 0) < 1 && !(product.deliverytime > 0)) continue
      if (!Number.isFinite(product.price) || product.price <= 0) continue
      seen.add(key)
      result.push(candidate)
      if (result.length >= limit) return result
    }
  return result
}

/** Re-read the product immediately before adding. Only the exact recommended
 * combination is eligible; never silently pick a cheaper/different variant. */
export const buildRecommendationCartItem = (
  product: tProduct,
  key: string,
  items: tItems,
): tItem | null => {
  if (product.inquireOnly || product.purchaseMode === 'inquiry') return null
  const raw = product.combinations.find(combination => combination.key === key)
  if (!raw || raw.cultivable === false || !raw.hash || !Number.isFinite(raw.price) || raw.price <= 0) return null
  const selected: Record<number, number> = {}
  const attributesText: NonNullable<tItem['attributesText']> = []
  let onlyStock = product.onlyStock
  for (const attribute of product.attributes || []) {
    const options = attribute.options.filter(option => raw.options.includes(option.id))
    if (options.length !== 1) return null
    selected[attribute.id] = options[0].id
    attributesText.push({ titel: attribute.name, option: options[0].name })
    if (options[0].uncultivable) onlyStock = true
  }
  const currentQty = items[key]?.qty || 0
  if (onlyStock && (raw.stock || 0) < currentQty + 1) return null
  if ((raw.stock || 0) < currentQty + 1 && !(raw.deliverytime > 0)) return null
  const combination = getCombination({ ...product, combinations: [raw] }, selected)
  if (!combination) return null
  const livePrice = raw.discount && !isDiscountDateActive({ dateEnd: raw.discount.dateEnd })
    ? raw.price : combination.price
  const lock = isCartPriceLockValid(combination.priceLock) ? combination.priceLock : undefined
  const priceLock = lock && getCartPriceAmount(lock.price, 1, { onlyStock, stock: raw.stock })
    <= getCartPriceAmount(livePrice, 1, { onlyStock, stock: raw.stock }) ? lock : undefined
  return {
    id: product.id,
    categorie: product.categorie,
    key,
    path: product.path,
    name: product.name,
    brand: { id: product.brand, titel: product.brandTitel },
    price: priceLock?.price ?? livePrice,
    priceLock,
    qty: 1,
    attributes: selected,
    attributesText,
    image: combination.images[0],
    deliverytime: combination.deliverytime,
    stockDeliverytime: combination.stockDeliverytime,
    stock: combination.stock,
    shippingType: product.shippingType,
    purchaseMode: product.purchaseMode || 'cart',
    onlyStock,
    hash: combination.hash,
  }
}
